import { createContext, useContext, useEffect, useState } from "react";
import { API } from "@/lib/api";

const SYMBOL = { EUR: "€", USD: "$", GBP: "£", CHF: "CHF" };
const CCtx = createContext({
  currency: "EUR", setCurrency: () => {}, rates: { EUR: 1 },
  convert: (n) => n, convertTo: (n) => n, format: (n) => n,
  formatMoney: (n) => n, ready: false, asOf: null, source: null, stale: false,
});

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => localStorage.getItem("hh_ccy") || "EUR");
  const [rates, setRates] = useState({ EUR: 1 });
  // Data e origine del cambio, non solo i numeri: la vetrina li cita accanto
  // all'importo, e un cambio senza data è un cambio che nessuno può verificare.
  const [meta, setMeta] = useState({ asOf: null, source: null, stale: false });
  const [ready, setReady] = useState(false);
  useEffect(() => { localStorage.setItem("hh_ccy", currency); }, [currency]);
  useEffect(() => {
    let cancelled = false;
    // I tassi si leggono dal backend (`GET /api/fx/rates`), non da una fonte
    // scelta dal browser: sono gli stessi con cui il checkout addebita —
    // stessa data, stesso valore, stessa cache. Chiedendoli direttamente a
    // Frankfurter la pagina poteva mostrare un cambio appena pubblicato mentre
    // il server ne usava ancora uno precedente, e la cifra letta dall'ospite
    // non era quella pagata.
    fetch(`${API}/fx/rates`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`fx ${r.status}`))))
      .then((d) => {
        if (cancelled) return;
        const next = { EUR: 1 };
        Object.entries(d.rates || {}).forEach(([code, value]) => {
          const n = Number(value);
          if (Number.isFinite(n) && n > 0) next[String(code).toUpperCase()] = n;
        });
        setRates(next);
        setMeta({ asOf: d.date || null, source: d.source || null, stale: Boolean(d.stale) });
        setReady(true);
      })
      .catch(() => { if (!cancelled) setReady(false); });
    return () => { cancelled = true; };
  }, []);
  // `convertTo` serve alla valuta **di incasso** della struttura, che non è
  // quella scelta per leggere i prezzi: la vetrina mostra entrambe.
  const convertTo = (amountEur, ccy) => Number(amountEur) * (rates[String(ccy || "EUR").toUpperCase()] || 1);
  const convert = (eur) => convertTo(eur, currency);
  const format = (eur, opts = {}) => {
    const active = currency !== "EUR" && !ready ? "EUR" : currency;
    const val = Number(eur) * (rates[active] || 1);
    const digits = opts.digits ?? (val >= 100 ? 0 : 2);
    return `${SYMBOL[active] || active} ${val.toFixed(digits)}`;
  };
  // Un importo già nella sua valuta, senza convertirlo: è così che si scrive
  // «£ 120,50» definitivo accanto a «€ 142,00» di listino.
  const formatMoney = (amount, ccy) => {
    const code = String(ccy || "EUR").toUpperCase();
    return `${SYMBOL[code] || code} ${Number(amount || 0).toFixed(2)}`;
  };
  return (
    <CCtx.Provider value={{
      currency, setCurrency, rates, convert, convertTo, format, formatMoney,
      ready, asOf: meta.asOf, source: meta.source, stale: meta.stale,
    }}>
      {children}
    </CCtx.Provider>
  );
}

export const useCurrency = () => useContext(CCtx);
export const CURRENCIES = ["EUR", "USD", "GBP", "CHF"];
